import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { ScreenshotReportsPort } from '../../../app/ports/reports/screenshot-reports.port';
import { ScreenshotReportRequest } from '../../../domain/models/reports/request/screenshot-report.request';
import { ScreenshotReportResponse } from '../../../domain/models/reports/screenshot-report.model';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';
import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';
import { mapSdkScreenshotReportsResponseToDomain } from '../../mappers/reports/screenshot-reports.mapper';
import { screenshotReportsSdkMock } from '../../mocks/report/screenshot-reports.mock';

type ScreenshotReportsMockAction = 'getEmployeeScreenshots';

@Injectable({ providedIn: 'root' })
export class ScreenshotReportsPortAdapter implements ScreenshotReportsPort {
    constructor(
        private screenshotReportsService: ReportSdk.ScreenshotReportsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getEmployeeScreenshots(payload: ScreenshotReportRequest): Observable<ScreenshotReportResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-screenshots-employee');

        const source$: Observable<ReportSdk.ScreenshotReportsResponse> = this.shouldMock('getEmployeeScreenshots')
            ? of(
                screenshotReportsSdkMock.getEmployeeScreenshots(
                    payload.employeeId,
                    payload.dateRange.startDate,
                    payload.dateRange.endDate,
                    payload.startTime,
                    payload.endTime,
                    payload.search,
                    payload.page ?? 0,
                    payload.size ?? 50,
                ),
            ).pipe(delay(360))
            : this.screenshotReportsService.getEmployeeScreenshots(
                payload.employeeId,
                payload.dateRange.startDate,
                payload.dateRange.endDate,
                payload.startTime || undefined,
                payload.endTime   || undefined,
                payload.search?.trim() || undefined,
                payload.page,
                payload.size,
            );

        return source$.pipe(
            map((resp) => mapSdkScreenshotReportsResponseToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-screenshots-employee')),
        );
    }

    private shouldMock(action: ScreenshotReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.screenshotReports?.[action]);
    }

    private showErrorToast(err: any): void {
        if (!this.toast) return;
        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}

